import { X } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';

export type FilterDimension = 'location' | 'salary' | 'company' | 'job' | 'neutral';

/**
 * Every dimension looks the same: what a filter is is said by its label (and a company's logo), not
 * by a hue, because hues mean something else here (green = good, iris = information). Only an
 * excluded filter is coloured, in coral.
 */
const DIMENSION: Record<FilterDimension, string> = {
  location: 'bg-jc-surface text-jc-ink ring-jc-border-control',
  salary: 'bg-jc-surface text-jc-ink ring-jc-border-control',
  company: 'bg-jc-surface text-jc-ink ring-jc-border-control',
  job: 'bg-jc-surface text-jc-ink ring-jc-border-control',
  neutral: 'bg-jc-surface text-jc-ink ring-jc-border-control',
};

export type FilterPillProps = {
  /** What the filter is about; exposed as data-dimension (all dimensions look the same: the label says which). */
  dimension?: FilterDimension;
  /** The filter's value. Long values truncate; the full text is in the tooltip. */
  label: string;
  /** Accessible name of the remove button, localised by the caller ("Remove filter: Helsinki"). */
  removeLabel: string;
  onRemove: () => void;
  /** An excluded (negated) filter: coral outline and struck-through label, whatever the dimension. */
  excluded?: boolean;
  /** Company filters carry the employer's logo, so the employer is recognised at a glance. */
  logoSrc?: string;
  className?: string;
};

/** A removable pill for one active filter, 32px tall, at most 240px wide. */
export function FilterPill({
  dimension = 'neutral',
  label,
  removeLabel,
  onRemove,
  excluded,
  logoSrc,
  className,
}: FilterPillProps) {
  return (
    <span
      title={label}
      data-dimension={dimension}
      className={cx(
        'inline-flex h-10 max-w-60 items-center gap-1.5 rounded-jc-control pr-2 text-sm font-semibold ring-1 ring-inset',
        logoSrc ? 'pl-2' : 'pl-4',
        excluded ? 'bg-jc-surface text-jc-ink ring-jc-ink' : DIMENSION[dimension],
        className,
      )}
    >
      {logoSrc ? (
        <span className="grid size-6 shrink-0 place-items-center overflow-hidden rounded-full bg-jc-logo-surface p-0.5 ring-1 ring-jc-border">
          <img src={logoSrc} alt="" className="max-h-full max-w-full object-contain" />
        </span>
      ) : null}
      <span className={cx('truncate', excluded && 'line-through')}>{label}</span>
      <button
        type="button"
        aria-label={removeLabel}
        onClick={onRemove}
        className={cx(
          'grid size-6 shrink-0 place-items-center rounded-full opacity-80 transition-opacity duration-(--jc-duration-fast) hover:opacity-100',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus',
        )}
      >
        <Icon icon={X} size="sm" />
      </button>
    </span>
  );
}
